import { apiGet, apiPut, apiDelete } from "../api";
import Logger from "@/utils/Logger";
import { createErrorHandler } from "@/utils/ApiErrorHandler";

// To be able to mock Toast in unit tests
// we need to call getToast dynamically within each method instead of initializing Toast globally.
import { getToast } from "@/hooks/useToast";

import {
  ApiResponse,
  CommodityThreshold,
  CommodityThresholdAudit,
  UpsertCommodityThresholdRequest,
  ResetCommodityThresholdRequest,
  ThresholdHistoryParams,
} from "@/interfaces/RiskThresholdTypes";

/**
 * Error handler for commodity threshold operations
 */
const handleCommodityThresholdError = createErrorHandler(
  "CommodityThresholdApi",
);

/**
 * API client for Commodity-Specific Risk Thresholds
 * Manages commodity-level threshold overrides that supersede global defaults
 */
export const CommodityThresholdApi = {
  /**
   * Get all commodity thresholds (defaults + overrides)
   * @returns Array of all commodity thresholds
   * @throws Error if request fails
   */
  getAllCommodityThresholds: async (): Promise<CommodityThreshold[]> => {
    try {
      Logger.info("Fetching all commodity thresholds", [
        "CommodityThresholdApi",
      ]);

      const response = await apiGet<ApiResponse<CommodityThreshold[]>>(
        "/risk-thresholds/commodities",
      );

      if (!response || !Array.isArray(response.responseObject)) {
        throw new Error("Invalid response structure from API");
      }

      const thresholds = response.responseObject;

      Logger.info(
        `Successfully fetched ${thresholds.length} commodity thresholds`,
        ["CommodityThresholdApi"],
      );

      // Cache in session storage
      try {
        sessionStorage.setItem(
          "commodityThresholds",
          JSON.stringify(thresholds),
        );
        Logger.debug("Cached commodity thresholds in session storage", [
          "CommodityThresholdApi",
        ]);
      } catch {
        Logger.warn("Failed to cache commodity thresholds", [
          "CommodityThresholdApi",
        ]);
      }

      return thresholds;
    } catch (error: unknown) {
      const errorInfo = handleCommodityThresholdError(
        error,
        "getAllCommodityThresholds",
      );
      getToast().error(errorInfo.message);
      throw error;
    }
  },

  /**
   * Get commodity threshold by ID
   * @param commodityId SCM Commodity ID
   * @returns Commodity threshold (default or override)
   * @throws Error if request fails
   */
  getCommodityThresholdById: async (
    commodityId: number,
  ): Promise<CommodityThreshold> => {
    try {
      Logger.info(`Fetching commodity threshold for ID: ${commodityId}`, [
        "CommodityThresholdApi",
      ]);

      const response = await apiGet<ApiResponse<CommodityThreshold>>(
        `/risk-thresholds/commodities/${commodityId}`,
      );

      if (!response || !response.responseObject) {
        throw new Error("Invalid response structure from API");
      }

      const threshold = response.responseObject;

      Logger.info(
        `Successfully fetched threshold for commodity: ${threshold.scmCommodityNm}`,
        ["CommodityThresholdApi"],
      );

      return threshold;
    } catch (error: unknown) {
      const errorInfo = handleCommodityThresholdError(
        error,
        `getCommodityThresholdById (ID: ${commodityId})`,
      );
      getToast().error(errorInfo.message);
      throw error;
    }
  },

  /**
   * Upsert commodity threshold override
   * Creates or updates commodity-specific threshold override
   * @param commodityId SCM Commodity ID
   * @param request Upsert request with threshold values
   * @returns Updated commodity threshold
   * @throws Error if validation fails or request fails
   */
  upsertCommodityThreshold: async (
    commodityId: number,
    request: UpsertCommodityThresholdRequest,
  ): Promise<CommodityThreshold> => {
    try {
      Logger.info(
        `Upserting commodity threshold for ID: ${commodityId}`,
        ["CommodityThresholdApi"],
        { request: request as unknown as Record<string, unknown> },
      );

      // Client-side validation
      const validationErrors = validateCommodityThresholdRequest(request);
      if (validationErrors.length > 0) {
        const errorMessage = `Validation failed: ${validationErrors.join(", ")}`;
        Logger.error(errorMessage, null, ["CommodityThresholdApi"]);
        throw new Error(errorMessage);
      }

      const response = await apiPut<ApiResponse<CommodityThreshold>>(
        `/risk-thresholds/commodities/${commodityId}`,
        request,
      );

      if (!response || !response.responseObject) {
        throw new Error("Invalid response structure from API");
      }

      const updatedThreshold = response.responseObject;

      Logger.info(
        `Successfully upserted threshold for commodity: ${updatedThreshold.scmCommodityNm}`,
        ["CommodityThresholdApi"],
      );

      return updatedThreshold;
    } catch (error: unknown) {
      const errorInfo = handleCommodityThresholdError(
        error,
        `upsertCommodityThreshold (ID: ${commodityId})`,
      );
      Logger.error(errorInfo.message, error, ["CommodityThresholdApi"]);
      throw error;
    } finally {
      // Clear cached thresholds to force fresh fetch
      sessionStorage.removeItem("commodityThresholds");
      sessionStorage.removeItem("activeThresholdDefaults");
    }
  },

  /**
   * Reset commodity threshold to defaults
   * Preserves override record for audit trail (sets IS_USING_DEFAULT = 1)
   * @param commodityId SCM Commodity ID
   * @param request Reset request with user name
   * @returns Updated commodity threshold (now using defaults)
   * @throws Error if request fails
   */
  resetCommodityThreshold: async (
    commodityId: number,
    request: ResetCommodityThresholdRequest,
  ): Promise<CommodityThreshold> => {
    try {
      Logger.info(
        `Resetting commodity threshold to defaults for ID: ${commodityId}`,
        ["CommodityThresholdApi"],
      );

      // Validate userName
      if (!request.userName || request.userName.trim() === "") {
        const errorMessage = "User name is required";
        throw new Error(errorMessage);
      }

      const response = await apiDelete<ApiResponse<CommodityThreshold>>(
        `/risk-thresholds/commodities/${commodityId}`,
        { data: request },
      );

      if (!response || !response.responseObject) {
        throw new Error("Invalid response structure from API");
      }

      const resetThreshold = response.responseObject;

      Logger.info(
        `Successfully reset threshold for commodity: ${resetThreshold.scmCommodityNm}`,
        ["CommodityThresholdApi"],
      );

      return resetThreshold;
    } catch (error: unknown) {
      const errorInfo = handleCommodityThresholdError(
        error,
        `resetCommodityThreshold (ID: ${commodityId})`,
      );
      Logger.error(errorInfo.message, error, ["CommodityThresholdApi"]);
      throw error;
    } finally {
      // Clear cached thresholds to force fresh fetch
      sessionStorage.removeItem("commodityThresholds");
      sessionStorage.removeItem("activeThresholdDefaults");
    }
  },

  /**
   * Get commodity threshold history
   * @param commodityId SCM Commodity ID
   * @param params Optional date range filters
   * @returns Array of historical threshold audit entries
   * @throws Error if request fails
   */
  getCommodityThresholdHistory: async (
    commodityId: number,
    params?: ThresholdHistoryParams,
  ): Promise<CommodityThresholdAudit[]> => {
    try {
      Logger.info(
        `Fetching commodity threshold history for ID: ${commodityId}`,
        ["CommodityThresholdApi"],
      );

      const queryParams = new URLSearchParams();
      if (params?.startDate) {
        queryParams.append("startDate", params.startDate);
      }
      if (params?.endDate) {
        queryParams.append("endDate", params.endDate);
      }

      const url = `/risk-thresholds/commodities/${commodityId}/history${
        queryParams.toString() ? `?${queryParams.toString()}` : ""
      }`;

      const response =
        await apiGet<ApiResponse<CommodityThresholdAudit[]>>(url);

      if (!response || !Array.isArray(response.responseObject)) {
        throw new Error("Invalid response structure from API");
      }

      const history = response.responseObject;

      Logger.info(
        `Successfully fetched ${history.length} historical entries for commodity ID: ${commodityId}`,
        ["CommodityThresholdApi"],
      );

      return history;
    } catch (error: unknown) {
      const errorInfo = handleCommodityThresholdError(
        error,
        `getCommodityThresholdHistory (ID: ${commodityId})`,
      );
      getToast().error(errorInfo.message);
      throw error;
    }
  },

  /**
   * Get cached commodity thresholds from session storage
   * @returns Cached thresholds or null if not available
   */
  getCachedCommodityThresholds: (): CommodityThreshold[] | null => {
    try {
      const cached = sessionStorage.getItem("commodityThresholds");
      if (cached) {
        Logger.debug("Loading commodity thresholds from cache", [
          "CommodityThresholdApi",
        ]);
        return JSON.parse(cached) as CommodityThreshold[];
      }
    } catch {
      Logger.warn("Failed to load cached commodity thresholds", [
        "CommodityThresholdApi",
      ]);
    }
    return null;
  },
};

/**
 * Validate commodity threshold request
 * Ensures ranges don't overlap and values are within bounds
 */
const validateCommodityThresholdRequest = (
  request: UpsertCommodityThresholdRequest,
): string[] => {
  const errors: string[] = [];

  // Validate highMax < mediumMin
  if (request.highMax >= request.mediumMin) {
    errors.push("High max must be less than Medium min");
  }

  // Validate mediumMax < lowMin
  if (request.mediumMax >= request.lowMin) {
    errors.push("Medium max must be less than Low min");
  }

  // Validate values are within 0-10 range
  if (request.highMax < 0 || request.highMax > 10) {
    errors.push("High max must be between 0 and 10");
  }
  if (request.mediumMin < 0 || request.mediumMin > 10) {
    errors.push("Medium min must be between 0 and 10");
  }
  if (request.mediumMax < 0 || request.mediumMax > 10) {
    errors.push("Medium max must be between 0 and 10");
  }
  if (request.lowMin < 0 || request.lowMin > 10) {
    errors.push("Low min must be between 0 and 10");
  }

  // Validate userName is provided
  if (!request.userName || request.userName.trim() === "") {
    errors.push("User name is required");
  }

  return errors;
};
